import { useEffect, useState } from 'react';
import { getSummary } from '|/app/services';
import useLoad from '|/hooks/use-load';
import { getSlatePurchaseStatus } from '|/services/slate-purchase-status';

const getNextRoute = async (abortSignal: AbortSignal) => {
  const status = await getSlatePurchaseStatus(abortSignal);

  // Case 1: no Slate purchase and no active Slate cart — nothing to resume.
  if (status.missingSlatePurchase && !status.hasActiveSlatePurchase) {
    return '/vehicle/missing';
  }

  // Case 2: a Slate purchase alongside an active Carvana purchase — send the customer
  // to the change-vehicle screen, which needs the vehicle id from the summary.
  if (status.hasActiveCarvanaPurchase && status.hasActiveSlatePurchase) {
    const summary = await getSummary(abortSignal);
    const vehicleId = summary?.vehicle?.id;
    if (!vehicleId) {
      throw new Error('Slate dealer determination: missing vehicle id for change-vehicle redirect');
    }
    return `/vehicle/confirmation/${vehicleId}`;
  }

  // Cases 3-5: an active Slate purchase resumes at Personal Details — whether
  // just started or previously started. Per backend, slatePurchaseStarted === true
  // always routes here, and a not-yet-started active purchase does too.
  if (status.hasActiveSlatePurchase) {
    return '/steps/personal-details';
  }

  return '/next-step';
};

const useConnectVm = () => {
  const load = useLoad();
  const [newRoute, setNewRoute] = useState<string | null>(null);

  useEffect(() => {
    const abortController = new AbortController();

    load(
      getNextRoute(abortController.signal).then(route => {
        setNewRoute(route);
      }),
      abortController.signal
    ).catch(() => void 0);

    return () => {
      abortController.abort();
    };
  }, [load]);

  return newRoute;
};

export default useConnectVm;
